import Axios from "axios";
import {
  SET_EDUCATIONAL_INFO_LIST,
  SET_EDUCATIONAL_INFO_DETAILS,
  UNSET_EDUCATIONAL_INFO_DETAILS,
  SET_EDUCATIONAL_INFO_LOADING,
} from '@/store/mutation-types';
import {
  getEducationalInfoList,
  getEducationalInfoById,
  getEducationalInfoByEmployee,
  saveUpdateEducationalInfo,
  deleteEducationalInfo,
  getEducationalInfoGroupedByEmployee,
  saveEmployeeEducationalInfo,
  updateEducationalInfo,
  bulkDeleteEducationalInfo,
} from '@/store/api';

// Initial state
const state = {
  educationalInfoList: [],
  educationalInfoDetails: null,
  loading: false,
}

const getters = {
  getEducationalInfoList(state) {
    return state.educationalInfoList
  },
  getEducationalInfoDetails(state) {
    return state.educationalInfoDetails
  },
  getEducationalInfoLoading(state) {
    return state.loading
  },
}

const mutations = {
  [SET_EDUCATIONAL_INFO_LIST](state, data) {
    state.educationalInfoList = data
  },
  [SET_EDUCATIONAL_INFO_DETAILS](state, data) {
    state.educationalInfoDetails = data
  },
  [UNSET_EDUCATIONAL_INFO_DETAILS](state) {
    state.educationalInfoDetails = null
  },
  [SET_EDUCATIONAL_INFO_LOADING](state, loading) {
    state.loading = loading
  },
}

const actions = {
  /**
   * Get educational information list
   */
  getEducationalInfoList({ commit }) {
    commit('SET_EDUCATIONAL_INFO_LOADING', true)
    return new Promise((resolve, reject) => {
      Axios.post(getEducationalInfoList)
        .then((response) => {
          if (response.data.success) {
            commit('SET_EDUCATIONAL_INFO_LIST', response.data.data)
          }
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          resolve(response.data)
        })
        .catch((error) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          reject(error)
        })
    })
  },

  /**
   * Get educational information by employee ID
   */
  getEducationalInfoByEmployee({ commit }, employeeId) {
    commit('SET_EDUCATIONAL_INFO_LOADING', true)
    return new Promise((resolve, reject) => {
      Axios.get(getEducationalInfoByEmployee(employeeId))
        .then((response) => {
          if (response.data.success) {
            commit('SET_EDUCATIONAL_INFO_LIST', response.data.data)
          }
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          resolve(response.data)
        })
        .catch((error) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          reject(error)
        })
    })
  },

  /**
   * Get educational information by ID
   */
  getEducationalInfoById({ commit }, id) {
    commit('SET_EDUCATIONAL_INFO_LOADING', true)
    return new Promise((resolve, reject) => {
      Axios.get(getEducationalInfoById(id))
        .then((response) => {
          if (response.data.success) {
            commit('SET_EDUCATIONAL_INFO_DETAILS', response.data.data)
          }
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          resolve(response.data)
        })
        .catch((error) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          reject(error)
        })
    })
  },

  /**
   * Save or update educational information
   */
  saveEducationalInfo({ commit }, educationalData) {
    commit('SET_EDUCATIONAL_INFO_LOADING', true)
    return new Promise((resolve, reject) => {
      Axios.post(saveUpdateEducationalInfo, educationalData)
        .then((response) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          resolve(response.data)
        })
        .catch((error) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          reject(error)
        })
    })
  },

  /**
   * Delete educational information
   */
  deleteEducationalInfo({ commit }, id) {
    commit('SET_EDUCATIONAL_INFO_LOADING', true)
    return new Promise((resolve, reject) => {
      Axios.delete(deleteEducationalInfo(id))
        .then((response) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          resolve(response.data)
        })
        .catch((error) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          reject(error)
        })
    })
  },

  /**
   * Get educational information grouped by level for an employee
   */
  getEducationalInfoGroupedByEmployee({ commit }, employeeId) {
    commit('SET_EDUCATIONAL_INFO_LOADING', true)
    return new Promise((resolve, reject) => {
      Axios.get(getEducationalInfoGroupedByEmployee(employeeId))
        .then((response) => {
          if (response.data.success) {
            commit('SET_EDUCATIONAL_INFO_DETAILS', response.data.data)
          }
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          resolve(response.data)
        })
        .catch((error) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          reject(error)
        })
    })
  },

  /**
   * Save multiple educational records for an employee
   */
  saveEmployeeEducationalInfo({ commit }, { employeeId, educationalData, files = {} }) {
    commit('SET_EDUCATIONAL_INFO_LOADING', true)
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      formData.append('employee_id', employeeId)
      formData.append('educational_data', JSON.stringify(educationalData))
      
      if (files.certificateFile) {
        formData.append('certificate_file', files.certificateFile)
      }
      if (files.transcriptFile) {
        formData.append('transcript_file', files.transcriptFile)
      }
      
      Axios.post(saveEmployeeEducationalInfo, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
        .then((response) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          resolve(response.data)
        })
        .catch((error) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          reject(error)
        })
    })
  },

  /**
   * Update educational information
   */
  updateEducationalInfo({ commit }, { id, educationalData }) {
    commit('SET_EDUCATIONAL_INFO_LOADING', true)
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      Object.keys(educationalData).forEach(key => {
        if (educationalData[key] !== null && educationalData[key] !== undefined) {
          formData.append(key, educationalData[key])
        }
      })
      
      Axios.put(updateEducationalInfo(id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
        .then((response) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          resolve(response.data)
        })
        .catch((error) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          reject(error)
        })
    })
  },

  /**
   * Bulk delete educational information
   */
  bulkDeleteEducationalInfo({ commit }, ids) {
    commit('SET_EDUCATIONAL_INFO_LOADING', true)
    return new Promise((resolve, reject) => {
      Axios.post(bulkDeleteEducationalInfo, { ids })
        .then((response) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          resolve(response.data)
        })
        .catch((error) => {
          commit('SET_EDUCATIONAL_INFO_LOADING', false)
          reject(error)
        })
    })
  },

  /**
   * Clear educational information details
   */
  clearEducationalInfoDetails({ commit }) {
    commit('UNSET_EDUCATIONAL_INFO_DETAILS')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
